import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styles from './index.module.scss';
import { useEditPageTrack } from '../../../hooks/useEditPageTrack';
import { useNotPassFieldToText, useSafeCheck } from '../../../hooks/useSafeCheck';
import { useAppSelector, AppState } from '../../../state';
import { track } from '../../../track';
import { MessageType, RoleType } from '../../../types';
import {
  Introduction,
  MessageItem,
  Sendbox,
  CustomAudio,
  LoadingMessageItem,
  CustomAudioPayload,
} from '../../Chat';
import { getNpcStatus } from '../../Home/NpcList';
import { LocalImage } from '../../Image';
import { message } from '../../message';
import { AudioStatus, generateLocalMid, IMessage, useChat } from '@mx/im';
import { APP_ID, getCommonParams } from '../../../request/params';
import { request } from '../../../request';

const PAGE = 'create_npc_advanced_page';

export const ChatArea = ({ bgImage }: { bgImage: string }) => {
  const { authData } = useAppSelector((state: AppState) => state.user);
  const {
    npcId: _npcId,
    settingInfo,
    voiceInfo,
    imgInfo,
    statusInfo,
    conversationNpcId,
  } = useAppSelector((state: AppState) => state.edit);

  const customAudioRef = useRef<any>(null);
  const sendboxRef = useRef<any>(null);
  const messageContainerRef = useRef<any>(null);
  const scrollTimer = useRef<any>();

  const npcId = conversationNpcId || _npcId;

  const shouldInsertFirstPrologue = useRef<boolean>(true);

  const { t } = useTranslation();

  const disableChat = useMemo(
    () => !settingInfo.npc_name || !settingInfo.desc || !settingInfo.prologue,
    [settingInfo.desc, settingInfo.npc_name, settingInfo.prologue]
  );

  const disableVoice = useMemo(() => {
    return !(voiceInfo && voiceInfo.npc_tone && Object.keys(voiceInfo.npc_tone)?.length);
  }, [voiceInfo]);

  const NotEnableAside = useCallback(
    () => (
      <MessageItem
        key={'welcome_aside'}
        data={{
          txt: t('should_init_tip'),
          type: MessageType.Aside,
          mid: 'welcome_aside',
          role_type: RoleType.AI,
        }}
        isLast={false}
      />
    ),
    []
  );

  const [initing, setIniting] = useState(true);
  const onConnectedCb = useCallback(() => {
    setIniting(false);
  }, []);

  const { trackParams } = useEditPageTrack();
  const onSendMessageSuccessCb = useCallback(
    (message: IMessage, msgType: string) => {
      track.collect('message_send', PAGE, {
        npc_id: npcId,
        dialog_model_choice: settingInfo.chat_model_type,
        ugc_npc_approving_status: getNpcStatus(statusInfo),
        message_id: message.custom_mid,
        message_type: msgType,
        plugin_list: trackParams.plugin_list,
      });
    },
    [npcId, settingInfo, statusInfo]
  );

  const onResetChatCb = useCallback(
    (message: IMessage) => {
      track.collect('chat_reset_success', PAGE, {
        npc_id: npcId,
        dialog_model_choice: settingInfo.chat_model_type,
        ugc_npc_approving_status: getNpcStatus(statusInfo),
        message_id: message.custom_mid,
      });
      appendCustomMessage({
        txt: t('edit_chat_preview_reset_tip'),
        type: MessageType.Reset,
        mid: generateLocalMid(),
      } as any);
      scrollToLastMessage();
    },
    [npcId, settingInfo, statusInfo]
  );

  const onReceiveAsideMessage = (_message: Partial<IMessage>) => {
    setMessageList((messageList) => [...(messageList || []), _message as IMessage]);
    scrollToLastMessage();
  };

  const scrollToLastMessage = useCallback(() => {
    if (messageContainerRef && messageContainerRef.current) {
      scrollTimer.current && clearTimeout(scrollTimer.current);
      scrollTimer.current = setTimeout(() => {
        messageContainerRef.current.scrollTo({
          top: messageContainerRef.current.scrollHeight,
          left: 0,
          behavior: 'smooth',
        });
      }, 200);
    }
  }, []);

  const commonParams = useMemo(() => {
    return getCommonParams(authData);
  }, [authData]);

  const {
    loading,
    hasLoading,
    onSendMessage,
    messageList,
    setMessageList,
    setFirstPrologue,
    onUpdateAudioStatus,
    appendCustomMessage,
    onResetChat,
    initIMEventHandler,
  } = useChat({
    npcId,
    imConfig: {
      wsParams: {
        token: authData.refreshToken!,
        device_id: authData.deviceId!,
        app_id: APP_ID,
      },
      request: (url, params) => {
        return request.post(url, params);
      },
    },
    commonParams,
    autoInit: true,
    fromWorkspace: true,
  });

  console.log('==messageList==', messageList);

  useEffect(() => {
    initIMEventHandler({
      onConnectedCb,
      onResetChatCb,
      onSendMessageSuccessCb,
      onReceiveAsideCb: onReceiveAsideMessage,
      onUpdateMessageListCb: scrollToLastMessage,
    });
  }, []);

  useEffect(() => {
    if (hasLoading) {
      scrollToLastMessage();
    }
  }, [hasLoading]);

  const onClickPlayAudio = useCallback(
    (payload: CustomAudioPayload) => {
      if (payload.status === AudioStatus.Playing) {
        customAudioRef.current.pause();
      } else {
        customAudioRef.current.play(payload);
      }
      setMessageList((prev) =>
        prev.map((msg) => ({
          ...msg,
          wav_status: AudioStatus.Ready,
        }))
      );
    },
    [setMessageList]
  );

  // useEffect(() => {
  //   if (settingInfo.npc_agent_type) npcAgentType.current = settingInfo.npc_agent_type;
  //   // setNpcId(npcId);
  // }, [npcAgentType, npcId, settingInfo.npc_agent_type]);

  useEffect(() => {
    if (npcId) {
      onResetChat();
      shouldInsertFirstPrologue.current = false;
    }
  }, []);

  // 更新npc_im_account
  // useEffect(() => {
  //   if (imInfo) {
  //     setNpcImAccount(imInfo.im_account);
  //   }
  // }, [imInfo.im_account]);

  useEffect(() => {
    if (settingInfo.prologue) {
      setFirstPrologue(settingInfo.prologue, shouldInsertFirstPrologue.current);
    }
  }, [settingInfo.prologue]);

  // useEffect(() => {
  //   initIM({
  //     imAccount: authData?.imAccountInfo?.account as string,
  //     imPassword: authData?.imAccountInfo?.password as string,
  //   });
  // }, []);

  const uploadParams = useMemo(() => {
    return {
      userId: authData.userId,
      npcId,
    };
  }, [authData.userId, npcId]);

  const lastMessage = useMemo(() => {
    if (!messageList?.length) return null;
    let hasUserMsg = false;
    for (let i = messageList.length - 1; i > -1; i--) {
      const item = messageList[i];
      if (
        item.role_type === RoleType.AI &&
        (item.type === MessageType.Text || item.type === MessageType.Voice) &&
        !item.is_open &&
        !item.discarded &&
        !hasUserMsg
      ) {
        return item;
      } else if (item.role_type === RoleType.User) {
        hasUserMsg = true;
      }
    }
  }, [messageList]);

  const { checking, safeCheck } = useSafeCheck();
  const { getNotPassText } = useNotPassFieldToText();
  const onPlayAudio = useCallback(
    async (payload: CustomAudioPayload) => {
      if (checking) return;
      onUpdateAudioStatus(payload.mid, AudioStatus.Loading);
      const notPassFields = await safeCheck();
      onUpdateAudioStatus(payload.mid, AudioStatus.Ready);
      if (notPassFields.length > 0) {
        const notPassText = getNotPassText(notPassFields);
        message.error(notPassText.join(' ') + ' ' + t('not_pass_text'));
      } else {
        onClickPlayAudio(payload);
      }
    },
    [checking, getNotPassText, onUpdateAudioStatus, onClickPlayAudio, safeCheck, t]
  );

  const handleResetChat = useCallback(() => {
    track.send('reset_button_click', PAGE, {
      npc_id: npcId,
      dialog_model_choice: settingInfo.chat_model_type,
      ugc_npc_approving_status: getNpcStatus(statusInfo),
      message_id: messageList?.[messageList.length - 1]?.custom_mid,
    });

    onResetChat?.();
  }, [npcId, settingInfo, statusInfo, messageList]);

  const [isSendingImage, setIsSendingImage] = useState(false);
  useEffect(() => {
    if (isSendingImage) {
      scrollToLastMessage();
    }
  }, [isSendingImage]);

  return (
    <div className={styles.chatContainer}>
      <LocalImage src={bgImage} className={styles.bg} />
      {imgInfo.base_img_url ? <img src={imgInfo.base_img_url} className={styles.bg} /> : null}
      <div className={styles.shadow} />
      <div className={styles.tip}>{t('edit_chat_preview_welcome_tip')}</div>
      <div style={{ height: 48 }} />
      <div
        ref={messageContainerRef}
        className={styles.messageContainer}
        style={{ paddingBottom: isSendingImage ? 100 : 12 }}
      >
        {disableChat ? (
          <NotEnableAside />
        ) : (
          <>
            {/* 简介 */}
            {!!settingInfo && !!settingInfo.desc ? <Introduction txt={settingInfo.desc} /> : null}
            {/* 消息记录 */}
            {messageList.map((msg) => (
              <MessageItem
                key={msg.mid}
                data={msg as any}
                showVoiceDuration={false}
                onPlayAudio={onPlayAudio}
                isLast={lastMessage?.mid === msg.mid}
                fromWorkspace={true}
                disableVoice={disableVoice}
              />
            ))}
            {hasLoading ? <LoadingMessageItem /> : null}
          </>
        )}
      </div>
      <Sendbox
        ref={sendboxRef}
        styles={styles}
        loading={loading}
        uploadParams={uploadParams}
        fromWorkspace={true}
        onSendMessage={(val, attachment) => {
          onSendMessage(val, attachment);
          sendboxRef.current?.setInputValue('');
          sendboxRef.current?.clearAttachment();
          scrollToLastMessage();
        }}
        onResetChat={handleResetChat}
        appendCustomAsideMessage={appendCustomMessage}
        safeCheck={safeCheck}
        checking={checking || initing || hasLoading}
        disabled={disableChat}
        setIsSendingImage={setIsSendingImage}
      />
      <CustomAudio
        ref={customAudioRef}
        onAudioPlayEnd={(mid) => {
          onUpdateAudioStatus(mid, AudioStatus.Ready);
        }}
        onAudioPause={(mid) => {
          onUpdateAudioStatus(mid, AudioStatus.Ready);
        }}
        onAudioPlaying={(mid) => {
          onUpdateAudioStatus(mid, AudioStatus.Playing);
        }}
        onAudioWaiting={(mid) => {
          onUpdateAudioStatus(mid, AudioStatus.Loading);
        }}
        onAudioError={(mid) => {
          onUpdateAudioStatus(mid, AudioStatus.Reload);
        }}
      />
    </div>
  );
};
